/**
 * @file StatCard.tsx
 * @brief KPI tile used across Analytics / Dashboard / Reports.
 *
 * @details
 * Two surfaces:
 * - `card` (default): a soft neutral Card
 * - `cell`: flat content for `GridBlock` / `GridCell`, no wash of its own
 *
 * Layout reserves a hint line so cards with / without hints share the
 * same height in a KPI row (e.g. Billing anomalies “Pool overage”).
 *
 * @author DES
 * @date 26-05-2026
 * @updated 29-07-2026 — tone washes + KPI text spacing
 * @updated 03-10-2026: optional `delta` pill and `to` link (company dashboard);
 *          neutral surfaces, red icon, `attention` for numbers needing action
 */

import type { ComponentType, ReactNode } from "react";
import { Link } from "react-router-dom";
import { TrendingDown, TrendingUp } from "lucide-react";
import { Card } from "@/components/ui/Card";
import { Skeleton } from "@/components/ui/Skeleton";
import { cn } from "@/shared/theme/cn";

type Tone = "brand" | "success" | "warning" | "info" | "muted";

interface StatCardProps {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  icon?: ComponentType<{ className?: string }>;
  tone?: Tone;
  isLoading?: boolean;
  className?: string;
  /** Flat grid-cell surface when nested in `GridCell`. Default `card`. */
  variant?: "card" | "cell";
  /**
   * % change vs a comparison window, as a pill before the hint (03-10-2026).
   * `null` = nothing to compare against ("New"); omitted = no pill.
   */
  delta?: number | null;
  /** Which way is good news for `delta` (cost: "down"). Default "up". */
  deltaGoodWhen?: "up" | "down";
  /** Makes the whole card a link to its page (03-10-2026). */
  to?: string;
  /** The number needs action: shown in the tone's colour (03-10-2026). */
  attention?: boolean;
}

function DeltaPill({
  delta,
  goodWhen = "up",
}: {
  delta: number | null;
  goodWhen?: "up" | "down";
}) {
  const up = delta == null || delta >= 0;
  const good = delta == null || delta === 0 || (goodWhen === "up") === up;
  const Icon = up ? TrendingUp : TrendingDown;
  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold tabular-nums",
        good ? "bg-success-soft text-success" : "bg-destructive/15 text-destructive",
      )}
    >
      <Icon className="h-3 w-3" aria-hidden />
      {delta == null ? "New" : `${delta > 0 ? "+" : ""}${delta}%`}
    </span>
  );
}

// Neutral cells (DES 03-10-2026: "pehle 3 karo"). Three loud washes (red,
// green, amber) read as alarms: red on "Active Employees", amber on £0.00.
// Now every cell is the page's own surface with a red icon, as in the nav,
// and the number in the text colour. Colour is kept for meaning only: the
// delta pill, and `attention` for a number that needs action (failed
// payments). `tone` is still accepted so callers compile; it only picks the
// `attention` colour.
const TONE_ATTENTION: Record<Tone, string> = {
  brand: "text-destructive",
  success: "text-success",
  warning: "text-warning",
  info: "text-info",
  muted: "text-foreground",
};

export function StatCard({
  label,
  value,
  hint,
  icon: Icon,
  tone = "brand",
  isLoading,
  className,
  variant = "card",
  delta,
  deltaGoodWhen = "up",
  to,
  attention = false,
}: StatCardProps) {
  const hasDelta = delta !== undefined && !isLoading;
  const body = (
    <div className="flex min-h-[6.25rem] items-stretch">
      {Icon ? (
        <div
          className={cn(
            "flex w-16 shrink-0 items-center justify-center border-r border-border/60 sm:w-[4.5rem]",
          )}
        >
          <Icon className="h-6 w-6 text-brand sm:h-7 sm:w-7" />
        </div>
      ) : null}
      <div className="flex min-w-0 flex-1 flex-col justify-center gap-1 px-4 py-4 md:px-5">
        {isLoading ? (
          <Skeleton className="h-7 w-24" />
        ) : (
          <p
            className={cn(
              "truncate text-2xl font-bold leading-none tabular-nums tracking-tight",
              attention ? TONE_ATTENTION[tone] : "text-foreground",
            )}
          >
            {value}
          </p>
        )}
        <p className="truncate text-sm font-medium leading-snug text-foreground/80">
          {label}
        </p>
        <div
          className={cn(
            "flex min-h-[1rem] min-w-0 items-center gap-2 text-xs leading-snug text-muted-foreground",
            !hint && !hasDelta && "invisible",
          )}
          aria-hidden={!hint && !hasDelta ? true : undefined}
        >
          {hasDelta ? <DeltaPill delta={delta ?? null} goodWhen={deltaGoodWhen} /> : null}
          <span className="truncate">{hint ?? "\u00a0"}</span>
        </div>
      </div>
    </div>
  );

  if (variant === "cell") {
    const cls = cn("h-full", className);
    return to ? (
      <Link
        to={to}
        className={cn(cls, "block transition-colors hover:bg-secondary/50")}
        aria-label={label}
      >
        {body}
      </Link>
    ) : (
      <div className={cls}>{body}</div>
    );
  }

  return (
    <Card
      className={cn(
        "overflow-hidden border-0 bg-secondary/40 p-0 shadow-none",
        className,
      )}
    >
      {body}
    </Card>
  );
}
